/* eslint-disable @typescript-eslint/no-explicit-any */
import { Row } from '@tanstack/react-table';
import { Button, cn, Pill, Stack, Tooltip, TooltipContent, TooltipTrigger } from 'component-library';
import i18next from 'i18next';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GenericTable } from '@/components/widgets/o/GenericTable/GenericTable';
import { SortButton } from '@/components/widgets/o/GenericTable/SortButton';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';
import { squareMetersToHectares } from '@/utils/plot';
import { range } from '@/utils/range';
import { MetadataColumn } from '@/utils/table/mergeColumns';

import { useIsLimitedAccessUser } from '../../hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '../../hooks/useIsOrganizationMember';

export const ComplianceTargets = ({ category }: { category: 'impacts' | 'dependencies' }) => {
  const { t } = useTranslation();

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();
  const hideActions = isLimitedAccessUser || !isOrganizationMember;
  const [groupForAccess, setGroupForAccess] = useState<any>(null);

  const mockData = useMemo(() => {
    return range(1, 5).map((seed) => {
      return {
        name: `Long Title with a lot of text and specs ${category} ${seed}`,
        targets: range(1, seed).map((i) => ({ id: i, label: `Target ${i}` })),
        total_plots: Math.floor(Math.random() * 100),
        total_area: Math.floor(Math.random() * 1000),
        actions: [],
      };
    });
  }, [category]);

  const defaultColumns = useMemo<
    MetadataColumn<
      Partial<{
        name: string;
        targets: { id: number; label: string }[];
        total_plots: number;
        total_area: number;
        actions: string[];
      }>
    >[]
  >(() => {
    return [
      {
        accessorKey: 'actions',
        header: () => null,
        cell: ({ row }) => {
          if (hideActions) {
            return null;
          }

          return (
            <span className='self-end'>
              <ActionsCell row={row} confirmGroupAccess={() => setGroupForAccess(row.original)} />
            </span>
          );
        },
      },
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <SortButton column={column} className='-ml-1.5 px-0'>
            {t(`shared.plots.tableHeader.name`)}
          </SortButton>
        ),
        // cell: ({ row }) => <NameCell row={row} />,
      },
      {
        accessorKey: 'targets',
        header: ({ column }) => (
          <SortButton column={column} className='-ml-1.5 px-0'>
            {t(`global.widget.filters.button.filters`)}
          </SortButton>
        ),
        meta: {
          styles: {
            cell: 'py-2',
          },
        },
        cell: ({ row }) => <TargetsCell row={row} />,
      },
      {
        accessorKey: 'total_plots',
        header: ({ column }) => (
          <SortButton column={column} className='-ml-1.5 px-0'>
            {t(`global.widget.totalAssets`)}
          </SortButton>
        ),
        meta: {
          styles: {
            cell: 'text-center',
          },
        },
        cell: ({ getValue }) => <span>{getValue() as string}</span>,
      },
      {
        accessorKey: 'total_area',
        header: ({ column }) => (
          <SortButton column={column} className='-ml-1.5 px-0'>
            {t(`global.widget.totalSize`)}
          </SortButton>
        ),
        meta: {
          styles: {
            cell: 'text-center',
          },
        },
        cell: ({ getValue }) => (
          <span>{`${getDisplayNumber(squareMetersToHectares((getValue() as number) ?? 0), i18next.language) as string} ha`}</span>
        ),
      },
    ];
  }, [t, hideActions]);

  return (
    <>
      <GenericTable
        title={t('shared.disclosure.labels.scienceBasedTargets')}
        data={mockData}
        defaultColumns={defaultColumns}
        className={cn('border-none shadow-none')}
        styles={{
          title: 'typography-overline text-lg text-text-secondary font-normal',
        }}
      />
      {!isNullish(groupForAccess) && <></>}
    </>
  );
};

const FIXED_FILTERS_HEIGHT_PX = 36;

const TargetsCell = ({ row }: { row: Row<any> }) => {
  const { t } = useTranslation();

  const allTargets = row.original.targets;
  const totalPillsCount = allTargets?.length ?? 0;

  const [visibleCount, setVisibleCount] = useState(allTargets?.length ?? 0);

  const visibleTargetsRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = visibleTargetsRef.current;
    if (!container || visibleCount <= 0) {
      return;
    }

    const hasOverflow = container.scrollHeight > container.clientHeight;
    if (hasOverflow) {
      setVisibleCount((previous: any) => Math.max(0, previous - 1));
    }
  }, [visibleCount, totalPillsCount]);

  const renderedPillsCount = visibleCount;
  const extraFilters = Math.max(0, totalPillsCount - renderedPillsCount);

  const visibleTargets = allTargets.slice(0, visibleCount);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Stack className='flex gap-1 py-0 justify-center'>
          <div
            ref={visibleTargetsRef}
            className='max-w-xs overflow-hidden'
            style={{ height: `${FIXED_FILTERS_HEIGHT_PX}px` }}
          >
            <Stack key='targets' className='flex gap-1 flex-row justify-start items-start'>
              {visibleTargets.map((target: any) => {
                return (
                  <Pill
                    size='small'
                    key={`target-${target.id}`}
                    className='bg-bg-light-grey typography-body2 text-text-secondary'
                  >
                    {target.label}
                  </Pill>
                );
              })}
            </Stack>
          </div>
          {extraFilters > 0 && (
            <span className='items-center w-fit bg-bg-light-grey typography-body2 rounded-lg text-text-secondary shrink-0 whitespace-nowrap px-1 py-1'>
              {t('global.widget.filters.sections.extraAdditionalFilters', { count: extraFilters })}
            </span>
          )}
        </Stack>
      </TooltipTrigger>
      <TooltipContent side='top' align='start' key='targets-tooltip'>
        <Stack className='gap-2'>
          {allTargets.map((target: any) => {
            return (
              <Pill
                size='small'
                key={`target-${target.id}`}
                className='bg-bg-light-grey typography-body2 text-text-secondary'
              >
                {target.label}
              </Pill>
            );
          })}
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
};

const ActionsCell = ({ row, confirmGroupAccess }: { row: Row<any>; confirmGroupAccess: (group: any) => void }) => {
  const group = row.original;
  const { t } = useTranslation();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant='text'
          onClick={() => confirmGroupAccess(group)}
          leftAdornment={<i className='ri-folder-shared-line text-2xl' />}
          className='py-1 px-1'
        />
      </TooltipTrigger>
      <TooltipContent side='top' align='start' key={`target-name-${group.id}`}>
        {t('shared.disclosure.targetToWorkstream.title')}
      </TooltipContent>
    </Tooltip>
  );
};
